import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { getQueryKey as getVmsQueryKey, type VirtualMachineLoc, vmQueryAtom } from "@/modules/compute/vmQuery.ts";
import type { DiskCloneMutation, DiskCreateMutation } from "@/modules/disks/disk.store.ts";
import { getAttachedResource } from "@/modules/disks/disk/resourceId.ts";
import { diskLink } from "@/modules/disks/links.ts";
import { atomAllLocationsQuery } from "@/modules/location/atomAllLocationsQuery.ts";
import { dismissToast, raiseProgressToast, raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getDiskTotalPrice, type ItemWithPrice } from "@/modules/pricing/resourcePricing.ts";
import { jsonEncodedBody, urlEncodedBody } from "@/utils/fetchClient.ts";
import { mergeLoadedQueries, type QMergedResult } from "@/utils/query/mergeQueries.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { produceQueryData } from "@/utils/query/produceQueryData.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import { type QueryClient, type QueryKey, queryOptions } from "@tanstack/react-query";
import type { Disk, DiskAttachDetachBody, DiskUpdateBody } from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries, mapOnValues, mergeMaps } from "@warrenio/utils/collections/maps";
import { MINUTES } from "@warrenio/utils/timeUnits";
import { atomFamily } from "jotai-family";
import { atom } from "jotai/vanilla";

//#region basic Types/Contracts
export interface Params {
    location: string;
    read_only_bootable?: boolean;
}

export interface DiskWithType extends Disk, Params {
    $type: "disk";
}

export interface DiskWithPrice extends DiskWithType, ItemWithPrice {
    $type: "disk";
    attached_to: VirtualMachineLoc | null;
}

type Response = Map<Disk["uuid"], DiskWithType>;

const emptyResponse: Response = new Map();

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("disk") };

//#endregion basic Types/Contracts

//#region helper functions

// NB: Need explicit return type to avoid TypeScript error for `UseQueryOptions` below.
export function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["disk/list"] : ["disk/list", params.location, params.read_only_bootable];
}

function diskFromData(data: Disk, location: string): DiskWithType {
    return { ...data, $type: "disk", location };
}

//#endregion helper functions

//#region Queries

function getSingleQuery(apiClient: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location, read_only_bootable } = params;
            const response = getResponseData(
                await apiClient.GET("/{location}/storage/disks", {
                    signal,
                    params: { path: { location }, query: { read_only_bootable } },
                }),
            );
            return mapFromEntries(response, (item) => [item.uuid, diskFromData(item, location)]);
        },
    });
}

const diskQueryAtom = atomFamily((location: string) => atomFromStandardQueryOptions(getSingleQuery, { location }));

export const privateDiskQueryAtom = atomFamily((location: string) =>
    atomFromStandardQueryOptions(getSingleQuery, { location, read_only_bootable: true }),
);

//#region Queries for disks with prices
type DisksWithPrices = Map<Disk["uuid"], DiskWithPrice>;

export const diskWithPricesQueryAtom = atomFamily((location: string) => {
    const diskQuery = diskQueryAtom(location);
    const vmQuery = vmQueryAtom(location);

    return atom((get): QMergedResult<DisksWithPrices | undefined> => {
        const results = {
            disks: get(diskQuery),
            vms: get(vmQuery),
            prices: get(pricesAtom),
        };
        return mergeLoadedQueries(results, ({ disks, prices, vms }) => {
            return mapOnValues(
                disks,
                (disk): DiskWithPrice => ({
                    ...disk,
                    attached_to: getAttachedResource(disk, [...vms.values()]),
                    price: getDiskTotalPrice(prices, disk),
                }),
            );
        });
    });
});

export const allDiskWithPricesQueryAtom = atomAllLocationsQuery(diskWithPricesQueryAtom, (results) =>
    mergeMaps(results),
);

//#endregion

//#endregion Queries

//#region Mutations
export function createDiskMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["disk/create"],
        async mutationFn({ body, location }: DiskCreateMutation) {
            const result = getResponseData(
                await api.POST("/{location}/storage/disks", {
                    ...urlEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
            return diskFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Disk created",
                error: "Error creating disk",
                successType: "success",
                viewLink: res ? diskLink(res) : undefined,
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function cloneDiskMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["disk/clone"],
        async mutationFn({ body, location }: DiskCloneMutation) {
            const result = getResponseData(
                await api.POST("/{location}/storage/disks", {
                    ...urlEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
            return diskFromData(result, location);
        },
        onMutate({ body, location, requestUuid }) {
            raiseDiskProgressToast(requestUuid, `Cloning disk [${body.display_name}]`);

            return { queryKey: getQueryKey({ location }) };
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(res, err, { location, requestUuid }) {
            dismissToast(requestUuid);

            raiseRequestToast(err, {
                ...toastOptions,
                success: "Disk cloned",
                error: "Error cloning disk",
                successType: "success",
                viewLink: res ? diskLink(res) : undefined,
            });

            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function modifyDiskMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["disk/modify"],
        async mutationFn({ body, location, uuid }: { body: DiskUpdateBody; location: string; uuid: string }) {
            const result = getResponseData(
                await api.PATCH("/{location}/storage/disks/{uuid}", {
                    ...urlEncodedBody,
                    body,
                    params: { path: { location, uuid } },
                }),
            );
            return diskFromData(result, location);
        },
        onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Disk updated",
                error: "Error updating disk",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function deleteDiskMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["disk/delete"],
        async mutationFn({ location, uuid }: { location: string; uuid: string }) {
            return getResponseData(
                await api.DELETE("/{location}/storage/disks/{uuid}", {
                    params: { path: { location, uuid } },
                }),
            );
        },
        async onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Disk deleted",
                error: "Error deleting disk",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) });
            await queryClient.invalidateQueries({ queryKey: getVmsQueryKey({ location }) });
        },
    });
}

export function attachDiskMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["disk/attach"],
        async mutationFn({ body, location }: { body: DiskAttachDetachBody; location: string }) {
            return getResponseData(
                await api.POST("/{location}/user-resource/vm/storage/attach", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
        },
        onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Disk attached",
                error: "Error attaching disk",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getVmsQueryKey({ location }) }));
        },
    });
}

export function detachDiskMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["disk/detach"],
        async mutationFn({ body, location }: { body: DiskAttachDetachBody; location: string }) {
            return getResponseData(
                await api.POST("/{location}/user-resource/vm/storage/detach", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
        },
        onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Disk detached",
                error: "Error detaching disk",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getVmsQueryKey({ location }) }));
        },
    });
}

//#endregion Mutations

const DISK_CREATE_TIMER = 3 * MINUTES;

function raiseDiskProgressToast(requestUuid: string, message: string) {
    raiseProgressToast({
        ...toastOptions,
        label: message,
        lifeTime: DISK_CREATE_TIMER,
        toastId: requestUuid,
    });
}
